import React, { useCallback, useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { FaCrown } from 'react-icons/fa';
import Countdown from 'react-countdown';
import ConfettiExplosion from 'react-confetti-explosion';
import humanizeDuration from 'humanize-duration';
import useRaffleDraw from '../hooks/useRaffleDraw';
import useRaffles from '../hooks/useRaffles';
import useDisplayNames from '../hooks/useDisplayName';

const countdownRenderer = ({ total, completed }) => {
  if (completed) return '';

  return humanizeDuration(total, { language: 'en', largest: 2, round: true });
};

const RaffleDraw = ({ raffleKey }) => {
  const draw = useRaffleDraw(raffleKey);

  const [drawTime, setDrawTime] = useState();
  const [waitingForDraw, setWaitingForDraw] = useState(false);
  const [liveDraw, setLiveDraw] = useState(false);

  useEffect(() => {
    if (
      draw.ready &&
      new Date(draw.state.drawTime) <= Date.now() &&
      !draw.state.drawHash
    ) {
      setLiveDraw(true);
      draw.post();
    }
  }, [draw]);

  useEffect(() => {
    if (draw.state.drawTime) {
      // const drawTime = new Date();
      // drawTime.setSeconds(drawTime.getSeconds() + 10);

      const drawTime = new Date(draw.state.drawTime);
      setDrawTime(drawTime);
    }
  }, [draw.state.drawTime]);

  const drawWinners = useCallback(async () => {
    setLiveDraw(true);
    setWaitingForDraw(false);
    const delay = Math.floor(Math.random() * 5000);
    setTimeout(draw.post, delay);
  }, [draw]);

  useEffect(() => {
    if (drawTime) setWaitingForDraw(drawTime.getTime() > Date.now());
  }, [drawTime]);

  useEffect(() => {
    // after draw time has passed, but draw results are not yet available
    if (!waitingForDraw && !draw.state.drawHash) {
      const intervalId = setInterval(draw.get, 5000); // poll for draw results
      return () => clearInterval(intervalId);
    }
  }, [waitingForDraw, draw]);

  const displayName = useDisplayNames();

  if (!draw.ready) return <Spinner animation="border" />;

  if (!drawTime)
    return (
      <div className="alert alert-dark text-center lead">
        This raffle has ended
      </div>
    );

  if (waitingForDraw) {
    return (
      <div className="alert alert-dark text-center lead">
        Drawing winners in{' '}
        <Countdown
          date={drawTime}
          onComplete={drawWinners}
          renderer={countdownRenderer}
        />
      </div>
    );
  }

  if (!draw.state.drawHash) {
    return (
      <div className="alert alert-dark text-center lead">
        <div>Raffle draw in progress...</div>
        <div className="my-3">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  const multipleWinners = draw.state.winners.length > 1;

  return (
    <div className="text-center">
      <div className="d-flex justify-content-center">
        {liveDraw && <ConfettiExplosion />}
      </div>
      <div className="h2 fw-bold d-flex justify-content-center align-items-center mb-4">
        <FaCrown className="me-2" />
        Raffle {multipleWinners ? 'Winners' : 'Winner'}
      </div>
      <div>
        <ul className="list-group list-group-flush">
          {draw.state.winners.map((winner, index) => (
            <li key={index} className="list-group-item bg-transparent py-2">
              <div className="h4">{displayName(winner)}</div>
              <small className="text-muted">
                <pre className="m-0">{winner}</pre>
              </small>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const RaffleDrawPanel = ({ raffleKey }) => {
  const raffle = useRaffles(raffleKey);

  if (raffle.automated_draw) {
    return <RaffleDraw raffleKey={raffleKey} />;
  }

  return (
    <div className="alert alert-dark text-center lead">
      This raffle has ended
    </div>
  );
};

export default RaffleDrawPanel;
